
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { 
  Calendar, 
  Wrench, 
  DollarSign, 
  FileText, 
  Upload, 
  Save, 
  AlertTriangle,
  CheckCircle,
  X
} from 'lucide-react';
import PageLayout from '@/components/PageLayout';
import { useCar } from '@/contexts/CarContext';
import { useSettings } from '@/contexts/SettingsContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import CarCarousel from '@/components/CarCarousel';
import { 
  Card, 
  CardHeader, 
  CardContent, 
  CardFooter, 
  CardTitle, 
  CardDescription 
} from '@/components/ui/card';
import { 
  Dialog,
  DialogContent, 
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/hooks/use-toast';

interface IssueStatus {
  id: string;
  title: string;
  description: string;
  status: 'resolved' | 'partially_resolved' | null;
}

const LogRepairPage = () => {
  const { carId, repairId } = useParams<{ carId: string, repairId?: string }>();
  const { t } = useSettings();
  const navigate = useNavigate();
  const { 
    cars, 
    addRepair, 
    updateRepair, 
    deleteRepair, 
    getRepair,
    selectCar,
    selectedCarId
  } = useCar();

  const [title, setTitle] = useState('');
  const [date, setDate] = useState<Date>(new Date());
  const [mechanicName, setMechanicName] = useState('');
  const [cost, setCost] = useState('');
  const [description, setDescription] = useState('');
  const [receiptImage, setReceiptImage] = useState<string | undefined>();
  const [issueStatuses, setIssueStatuses] = useState<IssueStatus[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [addedByMechanic, setAddedByMechanic] = useState(false);
  const [mechanicAddedDate, setMechanicAddedDate] = useState<string | undefined>();

  const selectedCar = carId ? cars.find(c => c.id === carId) : undefined;

  // Handle car selection change
  const handleSelectCar = (newCarId: string) => {
    if (isEditing) {
      // Show confirmation if editing and trying to switch cars
      if (confirm("Changing cars will discard your current edits. Continue?")) {
        navigate(`/log-repair/${newCarId}`);
      }
    } else {
      // Just navigate to the new car's repair page
      navigate(`/log-repair/${newCarId}`);
    }
  };

  useEffect(() => {
    if (!selectedCar) {
      navigate('/cars');
      return;
    }

    selectCar(carId);

    if (repairId) {
      setIsEditing(true);
      const repair = getRepair(carId, repairId);
      
      if (repair) {
        setTitle(repair.title);
        setDate(new Date(repair.date));
        setMechanicName(repair.mechanicName || '');
        setCost(repair.cost ? repair.cost.toString() : '');
        setDescription(repair.description || '');
        setReceiptImage(repair.receiptImage);
        setAddedByMechanic(repair.addedByMechanic || false);
        setMechanicAddedDate(repair.mechanicAddedDate);
      } else {
        navigate(`/car-view/${carId}`);
      }
    }

    // Get unresolved or recently resolved issues
    if (selectedCar.issues && selectedCar.issues.length > 0) {
      const relevantIssues = selectedCar.issues
        .filter(issue => ['draft', 'open', 'recurred'].includes(issue.status as any) || 
          (issue.status === 'resolved' && new Date(issue.date) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)))
        .map(issue => ({
          id: issue.id,
          title: issue.title,
          description: issue.description,
          status: null as ('resolved' | 'partially_resolved' | null),
        }));

      // If editing, pre-select the previously selected issues
      if (isEditing && repairId) {
        const repair = getRepair(carId, repairId);
        if (repair) {
          relevantIssues.forEach((issue, index) => {
            if (repair.resolvedIssues?.includes(issue.id)) {
              relevantIssues[index].status = 'resolved';
            } else if (repair.partiallyResolvedIssues?.includes(issue.id)) {
              relevantIssues[index].status = 'partially_resolved';
            }
          });
        }
      }

      setIssueStatuses(relevantIssues);
    }
  }, [carId, repairId, selectedCar, navigate, selectCar, getRepair, isEditing]);

  const handleSaveRepair = () => {
    if (!selectedCar) return;
    if (!title.trim()) {
      toast({
        title: "Error",
        description: "Repair title is required",
        variant: "destructive",
      });
      return;
    }

    const repairData = {
      title,
      date: date.toISOString(),
      mechanicName: mechanicName.trim() || undefined,
      cost: cost ? parseFloat(cost) : undefined,
      description: description.trim() || undefined,
      receiptImage,
      resolvedIssues: issueStatuses
        .filter(issue => issue.status === 'resolved')
        .map(issue => issue.id),
      partiallyResolvedIssues: issueStatuses
        .filter(issue => issue.status === 'partially_resolved')
        .map(issue => issue.id),
      addedByMechanic,
      mechanicAddedDate,
    };

    if (isEditing && repairId) {
      updateRepair(carId, repairId, repairData);
      toast({
        title: "Success",
        description: "Repair updated successfully",
      });
    } else {
      addRepair(carId, repairData);
      toast({
        title: "Success",
        description: "Repair saved successfully",
      });
    }

    navigate(`/car-view/${carId}`);
  };

  const handleDelete = () => {
    if (isEditing && repairId) {
      deleteRepair(carId, repairId);
      toast({
        title: "Success",
        description: "Repair deleted successfully",
      });
      navigate(`/car-view/${carId}`);
    }
    setShowDeleteDialog(false);
  };

  const handleCancel = () => {
    navigate(`/car-view/${carId}`);
  };

  const toggleIssueStatus = (id: string) => {
    setIssueStatuses(prevStatuses => 
      prevStatuses.map(issue => {
        if (issue.id === id) {
          // Cycle through: null -> resolved -> partially_resolved -> null
          let newStatus: 'resolved' | 'partially_resolved' | null = 'resolved';
          
          if (issue.status === 'resolved') {
            newStatus = 'partially_resolved';
          } else if (issue.status === 'partially_resolved') {
            newStatus = null;
          }
          
          return { ...issue, status: newStatus };
        }
        return issue;
      })
    );
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // For now, we'll just use a placeholder for the receipt image
      // In a real app, this would upload to storage
      const reader = new FileReader();
      reader.onload = (event) => {
        setReceiptImage(event.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  if (!selectedCar) {
    return <div>Loading...</div>;
  }

  return (
    <PageLayout 
      title={isEditing ? "Edit Repair" : "Log Repair"} 
      showBackButton 
      onBack={handleCancel}
    >
      {cars.length > 1 && (
        <div className="mb-4">
          <h3 className="text-sm font-medium mb-2">Select Car</h3>
          <CarCarousel 
            cars={cars}
            selectedCarId={selectedCarId}
            mainCarId={null}
            onSelectCar={handleSelectCar}
          />
        </div>
      )}
      
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Wrench className="mr-2 h-5 w-5 text-lime" />
              Repair Details
            </CardTitle>
            <CardDescription>
              Log the details of the repair or maintenance service
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Repair Title *</Label>
              <Input 
                id="title" 
                placeholder="e.g., Brake pads replaced" 
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="date">Date of Repair *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    id="date"
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                  >
                    <Calendar className="mr-2 h-4 w-4" />
                    {date ? format(date, "PPP") : "Select date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <CalendarComponent
                    mode="single"
                    selected={date}
                    onSelect={(selectedDate) => selectedDate && setDate(selectedDate)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label htmlFor="mechanic">Garage/Mechanic Name</Label>
              <Input 
                id="mechanic" 
                placeholder="e.g., AllCarService" 
                value={mechanicName}
                onChange={(e) => setMechanicName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="cost">Cost</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <Input 
                  id="cost" 
                  type="number" 
                  placeholder="0.00" 
                  className="pl-10"
                  value={cost}
                  onChange={(e) => setCost(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description / Notes</Label>
              <Textarea 
                id="description" 
                placeholder="Any additional details about the repair..." 
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="h-24"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="receipt">Upload Receipt or File</Label>
              <div className="flex flex-col space-y-3">
                <label 
                  htmlFor="receipt-upload" 
                  className="flex cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-gray-300 p-4 transition-colors hover:border-gray-400"
                >
                  <Upload className="h-5 w-5 text-gray-400" />
                  <span className="text-sm text-gray-500">Upload image or PDF</span>
                  <input 
                    id="receipt-upload" 
                    type="file" 
                    className="hidden" 
                    accept="image/*,application/pdf"
                    onChange={handleFileUpload}
                  />
                </label>
                
                {receiptImage && (
                  <div className="relative inline-block">
                    <img 
                      src={receiptImage} 
                      alt="Receipt" 
                      className="h-20 w-auto object-cover rounded"
                    />
                    <button 
                      className="absolute -right-2 -top-2 rounded-full bg-background p-1 shadow"
                      onClick={() => setReceiptImage(undefined)}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {issueStatuses.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <AlertTriangle className="mr-2 h-5 w-5 text-soft-blue" />
                Link to Symptoms
              </CardTitle>
              <CardDescription>
                Select which issues were addressed by this repair
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {issueStatuses.map((issue) => (
                  <div 
                    key={issue.id} 
                    className="flex items-start space-x-3 rounded-md border p-3 transition-colors cursor-pointer hover:bg-gray-50"
                    onClick={() => toggleIssueStatus(issue.id)}
                  >
                    <div className="flex-1">
                      <div className="font-medium">{issue.title}</div>
                      <p className="text-sm text-muted-foreground line-clamp-1">
                        {issue.description}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      {issue.status === 'resolved' ? (
                        <Badge className="bg-lime text-black">
                          <CheckCircle className="mr-1 h-3 w-3" /> Resolved
                        </Badge>
                      ) : issue.status === 'partially_resolved' ? (
                        <Badge className="bg-orange-300 text-black">
                          <AlertTriangle className="mr-1 h-3 w-3" /> Partially
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-gray-400">
                          Not linked
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {addedByMechanic && (
          <div className="mt-4">
            <Badge variant="outline" className="bg-gray-100">
              Logged by {mechanicName || "Mechanic"} on {mechanicAddedDate ? format(new Date(mechanicAddedDate), "PP") : ""}
            </Badge>
          </div>
        )}

        <div className="flex flex-col gap-2">
          <Button 
            onClick={handleSaveRepair}
            className="bg-lime text-black hover:bg-lime/90"
          >
            <Save className="mr-2 h-4 w-4" />
            {isEditing ? "Update Repair" : "Save Repair"}
          </Button>
          
          <Button 
            variant="outline" 
            onClick={handleCancel}
          >
            Cancel
          </Button>
          
          {isEditing && (
            <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" className="text-red-500 border-red-200">
                  Delete Repair
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Are you sure you want to delete this repair?</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-muted-foreground">
                  This action cannot be undone. Any symptoms that were marked as resolved by this repair will be set back to "open" status.
                </p>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
                    Cancel
                  </Button>
                  <Button 
                    variant="destructive" 
                    onClick={handleDelete}
                  >
                    Delete
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>
    </PageLayout>
  );
};

export default LogRepairPage;
